import { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import * as S from './styles';

interface MultilineInputProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	value?: string;
	defaultValue?: string;
	variant?: 'medium' | 'large';
	minLines?: number;
	debug?: boolean; // Changes rendering in a way that helps debug. Not for production use!
	readonly?: boolean;
	renderBorder?: boolean;
	onFocusChange?: (isFocused: boolean) => void;
	onContextMenu?: (e: React.MouseEvent) => void;
}

function MultilineInput(
	{
		value: valueFromProps,
		defaultValue,
		variant = 'medium',
		minLines = 1,
		debug = false,
		readonly = false,
		renderBorder = true,
		onFocusChange,
		onContextMenu,
		...textareaProps
	}: MultilineInputProps,
	ref: React.Ref<HTMLTextAreaElement>
) {
	const fieldRef = useRef<HTMLTextAreaElement>(null);

	// Expose internal ref to <textarea> (fieldRef) as a ref to the parent component.
	useImperativeHandle(ref, () => fieldRef.current!, []);

	const isControlled = typeof valueFromProps != 'undefined';
	const hasDefaultValue = typeof defaultValue != 'undefined';

	// Local value; only used when the component is uncontrolled.
	const [localValue, setLocalValue] = useState(hasDefaultValue ? defaultValue : '');

	const value = isControlled ? valueFromProps : localValue;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		// If uncontrolled, we need to update our local value.
		if (!isControlled) {
			setLocalValue(e.currentTarget.value);
		}

		textareaProps.onChange?.(e);
		if (e.isDefaultPrevented()) return;
	};

	const onFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
		onFocusChange?.(true);
		textareaProps.onFocus?.(e);
	};

	const onBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
		onFocusChange?.(false);
		textareaProps.onBlur?.(e);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<S.Wrapper variant={variant} disabled={readonly} renderBorder={renderBorder}>
			<S.Field
				{...textareaProps}
				as="textarea"
				ref={fieldRef}
				value={value}
				style={{
					color: debug ? 'red' : 'transparent',
					textShadow: debug ? '0 0 3px red' : 'none',
				}}
				{...textareaProps}
				onChange={onChange}
				onFocus={onFocus}
				onBlur={onBlur}
				onMouseDown={(e) => {
					if (e.button === 2) {
						// Right mouse button
						e.preventDefault();
						e.stopPropagation();
						onContextMenu?.(e as any);
					}
				}}
				readonly={readonly}
			/>
			<S.Sizer minLines={minLines} readonly={readonly}>
				{value}
				{/* The sizer text needs to have something on the last line if the last line is empty to
				match the rendered height of the text area  */}
				{value === '' || value.endsWith('\n') ? ' ' : ''}
			</S.Sizer>
		</S.Wrapper>
	);
}

export default forwardRef(MultilineInput);
